import { ChevronDown } from 'lucide-react';
import { type MouseEvent, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import type { SelectOptionT } from '@/components/Select/types';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import structuredFilterUtils from '../utils/structuredFilter.utils';
import FieldItemOperatorSelectorModal from './FieldItemOperatorSelectorModal';

type Props = {
  dropdownKey: string;
  operator?: STRUCTURED_FILTER_OPERATOR;
  hasMultiValues: boolean; // 현재 선택된 값이 여러개인지
  isMultiValueField?: boolean; // 필드가 여러개 값을 가질 수 있는지
  onChange?: (value: STRUCTURED_FILTER_OPERATOR) => void;
  restriction?: 'include' | 'exclude';
};

const FieldItemOperatorSelector = (props: Props) => {
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const ref = useRef<HTMLDivElement>(null);

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (props.onChange) {
      setCurrDropdown(props.dropdownKey);
    }
  };

  const options: SelectOptionT[] = useMemo(() => {
    const getList = () => {
      if (props.restriction === 'include') {
        if (props.hasMultiValues) {
          return [
            STRUCTURED_FILTER_OPERATOR.OR,
            STRUCTURED_FILTER_OPERATOR.AND,
          ];
        } else {
          return [STRUCTURED_FILTER_OPERATOR.OR];
        }
      }
      if (props.restriction === 'exclude') {
        if (props.hasMultiValues) {
          return [STRUCTURED_FILTER_OPERATOR.NOR];
        } else {
          return [STRUCTURED_FILTER_OPERATOR.NOR];
        }
      }
      if (props.hasMultiValues && props.isMultiValueField) {
        return [
          STRUCTURED_FILTER_OPERATOR.OR,
          STRUCTURED_FILTER_OPERATOR.AND,
          STRUCTURED_FILTER_OPERATOR.NOR,
        ];
      }
      return [STRUCTURED_FILTER_OPERATOR.OR, STRUCTURED_FILTER_OPERATOR.NOR];
    };
    let list = getList();

    return list.map((key) => {
      const name = structuredFilterUtils.getOperatorTextMap({
        operator: key,
        hasMultiValues: props.hasMultiValues,
        isMultiValueField: props.isMultiValueField,
      });
      return {
        value: key,
        searchName: name,
      };
    });
  }, [props.hasMultiValues, props.isMultiValueField, props.restriction]);

  return (
    <>
      <Style onClick={onClick} ref={ref} g={1}>
        {structuredFilterUtils.getOperatorTextMap({
          operator: props.operator || STRUCTURED_FILTER_OPERATOR.OR,
          hasMultiValues: props.hasMultiValues,
          isMultiValueField: props.isMultiValueField,
        })}
        <ChevronDown className="size-3" />
      </Style>
      <FieldItemOperatorSelectorModal
        anchorRef={ref}
        dropdownKey={props.dropdownKey}
        selected={props.operator || STRUCTURED_FILTER_OPERATOR.OR}
        onChange={props.onChange}
        hasMultiValues={props.hasMultiValues}
        isMultiValueField={props.isMultiValueField}
        options={options}
      />
    </>
  );
};

const Style = styled(Flex)`
  color: ${(props) => props.theme.colors.gray600};
  align-items: center;
  height: 100%;
  background-color: transparent;
  transition: background-color 0.2s;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
`;

export default FieldItemOperatorSelector;
